import { App } from '@capacitor/app';
import { datadogRum } from '@datadog/browser-rum';
import { computed, shallowRef, watch } from 'vue';
import environment from '@/environment';
import { isMetaConsentAgeEligible } from '@/services/meta-app-events';
import { getConsentChoice } from '@/services/privacy-consents';
import { SESSION_REPLAY_OFF, readSessionReplayConfig, type SessionReplayRemoteConfig } from './session-replay-config';
import { resolveSessionReplaySampleRate } from './telemetry-env';

/**
 * Datadog Session Replay, gated on the signed-in account's Session Replay consent.
 *
 * RUM starts with `startSessionReplayRecordingManually`, so nothing is recorded until this
 * module starts it. It records only while ALL of these hold, and stops as soon as one does not:
 * - a signed-in account whose consents were loaded from the backend, with a stored, explicit
 *   Session Replay grant and no pending revocation (services/privacy-consents; a Meta grant
 *   is a separate purpose and never counts);
 * - the account is 18+ (no Replay for minors until the legal requirements are confirmed);
 * - a staging or production build (development and unknown builds never record);
 * - the remote kill switch is on and this app session falls within its sample rate;
 * - RUM was initialized.
 * Anything unknown, failed or timed out means not recording. Ordinary RUM is unaffected.
 */

export const isSessionReplayBuild = () => resolveSessionReplaySampleRate(environment.name) > 0;

/**
 * Offered only by a build that can record (staging, production), and with the same 18+ bar
 * (self-declared date of birth) as Meta, until legal confirms the rules for minors.
 */
export const isSessionReplayConsentOffered = (dateOfBirth?: string | null) =>
  isSessionReplayBuild() && isMetaConsentAgeEligible(dateOfBirth);

/** How long a kill-switch reading is trusted before it is read again. */
export const SESSION_REPLAY_CONFIG_TTL_MS = 5 * 60 * 1000;

type ReplayAccount = { id: string; dateOfBirth: string | null };

const account = shallowRef<ReplayAccount | null>(null);
let rumReady = false;
let recording = false;
let config: { value: SessionReplayRemoteConfig; readAt: number } | null = null;
let configRead: Promise<SessionReplayRemoteConfig> | null = null;
// One draw per app session (JS context), compared with the current remote rate.
let sampleDraw = Math.random() * 100;

const consentAllowsRecording = computed(() => {
  const current = account.value;
  return (
    !!current &&
    getConsentChoice(current.id, 'sessionReplay') === 'granted' &&
    isSessionReplayConsentOffered(current.dateOfBirth)
  );
});

/** Stops recording now (revocation, sign-out, kill switch). Never throws. */
export const stopSessionReplay = () => {
  if (!rumReady) return;
  recording = false;
  try {
    datadogRum.stopSessionReplayRecording();
  } catch {
    // Nothing more to stop.
  }
};

const getConfig = (force = false): Promise<SessionReplayRemoteConfig> => {
  if (!force && config && Date.now() - config.readAt < SESSION_REPLAY_CONFIG_TTL_MS) {
    return Promise.resolve(config.value);
  }
  if (!configRead) {
    configRead = readSessionReplayConfig()
      .catch(() => SESSION_REPLAY_OFF)
      .then(value => {
        config = { value, readAt: Date.now() };
        return value;
      })
      .finally(() => {
        configRead = null;
      });
  }
  return configRead;
};

const evaluate = async (forceConfig = false) => {
  if (!rumReady || !isSessionReplayBuild() || !consentAllowsRecording.value) {
    stopSessionReplay();
    return;
  }
  const remote = await getConfig(forceConfig);
  // Everything may have changed while the switch was read: check again before starting.
  const allowed =
    rumReady &&
    consentAllowsRecording.value &&
    remote.enabled &&
    sampleDraw < remote.sampleRate &&
    isSessionReplayBuild();
  if (!allowed) {
    stopSessionReplay();
    return;
  }
  if (recording) return;
  try {
    datadogRum.startSessionReplayRecording();
    recording = true;
  } catch {
    recording = false;
  }
};

// Synchronous: losing consent (revocation, pending revocation, sign-out, another account)
// stops the recording in the same tick, before any request is sent or answered. A recording
// never carries over from one account to another.
watch(
  () => (consentAllowsRecording.value ? account.value?.id ?? null : null),
  consentingAccountId => {
    stopSessionReplay();
    if (consentingAccountId) void evaluate();
  },
  { flush: 'sync' }
);

/** The signed-in account (GET /api/user), or null on sign-out. Only the id and birth date are kept. */
export const setSessionReplayAccount = (
  next: { id: string; profile?: { dateOfBirth?: string | null } | null } | null
) => {
  if (!next) {
    account.value = null;
    return;
  }
  const previous = account.value;
  // Like Meta: a response without a birth date cannot clear one already known for the account.
  const dateOfBirth = next.profile?.dateOfBirth || (previous?.id === next.id ? previous.dateOfBirth : null) || null;
  if (previous?.id !== next.id || previous.dateOfBirth !== dateOfBirth) account.value = { id: next.id, dateOfBirth };
};

/** Called by rum-logger once datadogRum.init returned. */
export const notifySessionReplayRumReady = () => {
  rumReady = true;
  void evaluate();
};

// Back in the foreground: re-read the kill switch, so turning it off stops recording
// without waiting for a restart.
void App.addListener('resume', () => {
  if (rumReady) void evaluate(true);
}).catch(() => undefined);

/** Test-only: the recording state as this module sees it. */
export const __isSessionReplayRecordingForTests = () => recording;

/** Test-only reset. */
export const __resetSessionReplayForTests = (draw = 0) => {
  account.value = null;
  rumReady = false;
  recording = false;
  config = null;
  configRead = null;
  sampleDraw = draw;
};
